import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { NotificationBell } from "@/components/NotificationBell";
import {
  Snowflake, LayoutDashboard, Building2, Map, Users, Share2, LogOut, ChevronsUpDown, CalendarClock,
  BookOpen, Tag, FileEdit, Wand2, CalendarDays, AlertTriangle, Users2, BookOpenCheck, Target, Rocket, LineChart,
  ShieldAlert, DatabaseBackup, CloudSun,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Panel", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/dashboard/leads", label: "Leads", icon: Users2, testid: "nav-leads" },
  { to: "/dashboard/guides", label: "Guías", icon: BookOpenCheck, testid: "nav-guides" },
  { to: "/dashboard/company", label: "Compañía", icon: Building2, testid: "nav-company" },
  { to: "/dashboard/territories", label: "Territorios", icon: Map, testid: "nav-territories" },
  { to: "/dashboard/team", label: "Equipo", icon: Users, testid: "nav-team" },
  { to: "/dashboard/connections", label: "Conexiones", icon: Share2, testid: "nav-connections" },
  { to: "/dashboard/publications", label: "Publicaciones", icon: CalendarClock, testid: "nav-publications" },
  { to: "/dashboard/knowledge", label: "Base de conocimiento", icon: BookOpen, testid: "nav-knowledge" },
  { to: "/dashboard/keywords", label: "Keywords", icon: Tag, testid: "nav-keywords" },
  { to: "/dashboard/content", label: "Contenido", icon: FileEdit, testid: "nav-content" },
  { to: "/dashboard/coverage", label: "Cobertura", icon: Target, testid: "nav-coverage" },
  { to: "/dashboard/campaigns", label: "Campañas", icon: Rocket, testid: "nav-campaigns" },
  // Prompt 49 (2026-10-09): blogs por temporada desde Search Console.
  { to: "/dashboard/seasonal", label: "Temporadas", icon: CloudSun, testid: "nav-seasonal" },
  { to: "/dashboard/creative-studio", label: "Estudio creativo", icon: Wand2, testid: "nav-creative-studio" },
  { to: "/dashboard/calendar", label: "Calendario", icon: CalendarDays, testid: "nav-calendar" },
  { to: "/dashboard/analytics", label: "Tráfico", icon: LineChart, testid: "nav-analytics" },
];

const PLATFORM_NAV = [
  { to: "/dashboard/clients", label: "Clientes", icon: ShieldAlert, testid: "nav-clients" },
  { to: "/dashboard/migration", label: "Migración", icon: DatabaseBackup, testid: "nav-migration" },
];

const ROLE_LABEL = { admin: "Admin", manager: "Manager", dispatcher: "Dispatcher", sales: "Sales" };

const PAGE_TITLE = {
  "/dashboard": "Panel",
  "/dashboard/leads": "Leads",
  "/dashboard/guides": "Guías",
  "/dashboard/company": "Compañía",
  "/dashboard/territories": "Territorios",
  "/dashboard/team": "Equipo",
  "/dashboard/connections": "Conexiones",
  "/dashboard/publications": "Publicaciones",
  "/dashboard/knowledge": "Base de conocimiento",
  "/dashboard/keywords": "Keywords",
  "/dashboard/content": "Contenido",
  "/dashboard/coverage": "Cobertura",
  "/dashboard/campaigns": "Campañas",
  "/dashboard/seasonal": "Temporadas",
  "/dashboard/creative-studio": "Estudio creativo",
  "/dashboard/calendar": "Calendario",
  "/dashboard/analytics": "Tráfico",
  "/dashboard/clients": "Clientes",
  "/dashboard/migration": "Migración",
};

export default function DashboardLayout() {
  const { user, tenants, activeTenantId, activeTenant, role, setActiveTenant, logout } = useAuth();
  const location = useLocation();
  const title = PAGE_TITLE[location.pathname] || "Panel";
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const [isTemporaryDomain, setIsTemporaryDomain] = useState(false);
  const navItems = user?.is_platform_operator ? [...NAV, ...PLATFORM_NAV] : NAV;

  useEffect(() => {
    if (!activeTenantId) return;
    api.get("/integrations/domain-status")
      .then((r) => setIsTemporaryDomain(!!r.data.is_temporary_domain))
      .catch(() => {});
  }, [activeTenantId]);

  return (
    <div className="min-h-screen flex bg-[#F7F7F8]">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 w-64 bg-[#09090B] border-r border-[#27272A] flex flex-col z-20" data-testid="sidebar">
        <div className="h-16 flex items-center gap-3 px-6 border-b border-[#27272A]">
          <div className="h-8 w-8 rounded-sm bg-[#0055FF] grid place-items-center shrink-0">
            <Snowflake className="h-4 w-4 text-white" strokeWidth={2} />
          </div>
          <span className="font-heading font-bold text-white tracking-tight">SprayOps</span>
        </div>

        {/* Selector de tenant */}
        <div className="p-4 border-b border-[#27272A]">
          <label className="text-[10px] uppercase tracking-wider text-[#71717A] font-medium">Tenant activo</label>
          <Select value={activeTenantId || ""} onValueChange={setActiveTenant}>
            <SelectTrigger
              data-testid="tenant-selector"
              className="mt-2 bg-[#18181B] border-[#27272A] text-white h-10 rounded-md focus:ring-[#0055FF]"
            >
              <SelectValue placeholder="Seleccionar tenant" />
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </SelectTrigger>
            <SelectContent>
              {tenants.map((t) => (
                <SelectItem key={t.tenant_id} value={t.tenant_id} data-testid={`tenant-option-${t.slug}`}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#0055FF] text-white"
                    : "text-[#A1A1AA] hover:bg-[#27272A] hover:text-white"
                }`
              }
            >
              <Icon className="h-4 w-4" strokeWidth={2} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-[#27272A] text-[10px] text-[#52525B] uppercase tracking-wider">
          UI: Español · Contenido: Inglés
        </div>
      </aside>

      {/* Contenido */}
      <div className="flex-1 ml-64 flex flex-col min-h-screen">
        <header className="sticky top-0 h-16 bg-white border-b border-[#E4E4E7] flex items-center justify-between px-8 z-10" data-testid="topbar">
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-lg font-bold text-[#09090B]">{title}</h1>
            {activeTenant && (
              <span className="text-xs text-[#71717A] border-l border-[#E4E4E7] pl-3">
                {activeTenant.name}
              </span>
            )}
          </div>

          <div className="flex items-center gap-4">
            <NotificationBell activeTenantId={activeTenantId} />
            {role && (
              <Badge
                data-testid="role-badge"
                className="bg-[#0055FF]/10 text-[#0055FF] hover:bg-[#0055FF]/10 border-0 text-[10px] uppercase tracking-wider rounded-sm"
              >
                {ROLE_LABEL[role] || role}
              </Badge>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 outline-none" data-testid="user-menu-trigger">
                  <Avatar className="h-8 w-8 rounded-md">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="rounded-md bg-[#09090B] text-white text-xs">{initials}</AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium text-[#09090B] hidden sm:block">{user?.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm font-medium">{user?.name}</div>
                  <div className="text-xs text-[#71717A] font-normal">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-button" onClick={logout} className="text-[#EF4444]">
                  <LogOut className="h-4 w-4 mr-2" /> Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-8">
          {activeTenant?.status === "suspended" && (
            <div
              data-testid="suspended-account-banner"
              className="mb-6 flex items-start gap-3 rounded-sm border border-[#EF4444]/30 bg-[#EF4444]/10 p-3 text-xs text-[#7F1D1D]"
            >
              <ShieldAlert className="h-4 w-4 text-[#EF4444] shrink-0 mt-0.5" />
              <span>
                <strong>Esta cuenta está suspendida.</strong> Contactá a soporte. No se puede generar
                ni publicar contenido nuevo mientras dure la suspensión; tus datos y leads siguen disponibles.
              </span>
            </div>
          )}
          {isTemporaryDomain && (
            <div
              data-testid="temporary-domain-banner"
              className="mb-6 flex items-start gap-3 rounded-sm border border-[#F59E0B]/30 bg-[#F59E0B]/10 p-3 text-xs text-[#92400E]"
            >
              <AlertTriangle className="h-4 w-4 text-[#F59E0B] shrink-0 mt-0.5" />
              <span>
                <strong>Dominio temporal.</strong> Los links de tracking de las publicaciones que salgan
                ahora van a dejar de funcionar cuando se migre al dominio final del cliente.
              </span>
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
